import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { useMutation } from '@apollo/client';
import { ControlBarButton } from '@m12s/component-library';
import { Box } from '@chakra-ui/react';
import { useEmbeddedEffect } from '@machinemetrics/mm-react-embeddable';
import { check, signOut, user, sync, handPointer } from 'lib/icons';
import { UtilityMenu } from 'components/UtilityMenu';
import useOperatorLogout from 'components/LaborTicket/hooks/useOperatorLogout';
import useSelectOperator from 'lib/hooks/useSelectOperator';
import MMEmbeddableZoneWithLoader from 'components/MMEmbeddableZoneWithLoader';
import MMDrawer from 'components/MMDrawer';
import EmbeddedMMDrawer from 'components/EmbeddedMMDrawer';
import { getMachine } from 'lib/selectors/getMachine';
import { getIsErpDriven } from 'lib/selectors/getIsErpDriven';
import { getEffectiveErpResources } from 'lib/selectors/getEffectiveErpResources';
import { getSevenDayLookbackStart } from 'lib/selectors/getSevenDayLookbackStart';
import { getCurrentOperator } from 'lib/selectors/getCurrentOperator';
import { getIsSetupModeActive } from 'lib/selectors/getIsSetupModeActive';
import { LABOR_TICKET_HOST } from 'lib/appConfig';
import { LABOR_TICKET_ACTIONS } from 'lib/constants';
import { throwErrorToast } from 'lib/utils/toast';
import { START_SETUP_MODE, END_SETUP_MODE } from 'lib/api/queries';
import {
  actionOpenLaborTicketModal,
  actionSetEmbeddedLaborTicket,
  actionSetSetupMode,
} from 'lib/actions';
import {
  MultiJobSelectFlow,
  SingleSelectJobFlow,
} from 'components/MultiJobSelect';
import { getHasCards } from 'lib/selectors/getHasCards';
import { getProgramEvents } from 'lib/selectors/getProgramEvents';
import { getTimeOptionsContext } from 'lib/selectors/getTimeOptionsContext';
import { CategorizeDowntime } from './ERPJobControlBar/CategorizeDowntime';
import {
  ControlBarResponsive,
  MobileIcon,
  ResponsiveText,
  Scrollable,
  SingleButtonScrollable,
} from './styled';

/**
 * Control bar for APM multi-job (palletized) machines.
 *
 * Production mode: Enter Setup, [Submit all when jobs running], Categorize, Sign out.
 * Setup mode: Enter Prod., Submit all / Select jobs, Categorize, Sign out.
 * ERP-driven machines never show Sign In / Sign Out — the ERP dispatches jobs
 * and labor, so signed-out operators get downtime categorization only (KGM-119).
 */
const ERPMultiJobControlBar = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const machine = useSelector(getMachine);
  const isErpDriven = useSelector(getIsErpDriven);
  const erpResources = useSelector(getEffectiveErpResources);
  const machineRef = machine?.machineRef;
  const lookbackStart = useSelector(getSevenDayLookbackStart);
  const operator = useSelector(getCurrentOperator);
  const currentOperatorErpId = operator?.erpId ?? null;
  const isSetupMode = useSelector(getIsSetupModeActive);
  const dispatch = useDispatch();
  const requestOperatorLogout = useOperatorLogout();
  const { openSelectOperator } = useSelectOperator();
  const [startSetupModeMutation] = useMutation(START_SETUP_MODE);
  const [endSetupModeMutation] = useMutation(END_SETUP_MODE);

  const hasCardsFromData = useSelector(getHasCards);
  const programEvents = useSelector(getProgramEvents);
  const timeOptionsContext = useSelector(getTimeOptionsContext);
  const hasOpenLaborTickets = useSelector((state) => {
    return state.workOrderManagement.hasOpenLaborTickets;
  });
  const hasOpenSetupLaborTickets = useSelector((state) => {
    return state.workOrderManagement.hasOpenSetupLaborTickets;
  });
  const [submitAllOpen, setSubmitAllOpen] = useState(false);
  const [selectJobsOpen, setSelectJobsOpen] = useState(false);
  const [addJobOpen, setAddJobOpen] = useState(false);
  const [openedFromSubmitAll, setOpenedFromSubmitAll] = useState(false);
  const [eventFormOpen, setEventFormOpen] = useState(false);
  const [eventFormContext, setEventFormContext] = useState(null);
  const [submitAllRefreshToken, setSubmitAllRefreshToken] = useState(0);
  /** True only when Submit-all was opened via Sign out (not the Submit all bar button). */
  const [submitAllOpenedFromSignOut, setSubmitAllOpenedFromSignOut] = useState(
    false
  );
  const [
    submitAllOpenedFromEnterProd,
    setSubmitAllOpenedFromEnterProd,
  ] = useState(false);
  const pendingItemCountRef = useRef(0);
  const pendingDismissIdRef = useRef(null);
  const [dismissedItemIds, setDismissedItemIds] = useState([]);

  const handleEnterSetup = useCallback(async () => {
    try {
      const { data } = await startSetupModeMutation({
        variables: { input: { machineRef } },
      });
      dispatch(actionSetSetupMode(data?.startSetupMode || null));
      window.dispatchEvent(new CustomEvent('apm:opdash-refresh'));
    } catch {
      // Mutation failed; setup mode state unchanged
    }
  }, [startSetupModeMutation, machineRef, dispatch]);

  const handleEnterProd = useCallback(async () => {
    if (!hasOpenSetupLaborTickets) {
      try {
        const { data } = await endSetupModeMutation({
          variables: { input: { machineRef } },
        });
        dispatch(actionSetSetupMode(data?.endSetupMode || null));
        window.dispatchEvent(new CustomEvent('apm:opdash-refresh'));
      } catch {
        // Mutation failed; remain in setup mode
      }
      return;
    }
    setDismissedItemIds([]);
    setSubmitAllOpenedFromEnterProd(true);
    setSubmitAllOpenedFromSignOut(false);
    setSubmitAllOpen(true);
  }, [endSetupModeMutation, machineRef, hasOpenSetupLaborTickets, dispatch]);

  const handleSignOut = useCallback(() => {
    if (!hasOpenSetupLaborTickets) {
      requestOperatorLogout();
      return;
    }
    setDismissedItemIds([]);
    setSubmitAllOpenedFromSignOut(true);
    setSubmitAllOpenedFromEnterProd(false);
    setSubmitAllOpen(true);
  }, [hasOpenSetupLaborTickets, requestOperatorLogout]);

  const handleSubmitAll = useCallback(() => {
    setDismissedItemIds([]);
    setSubmitAllOpenedFromSignOut(false);
    setSubmitAllOpenedFromEnterProd(false);
    setSubmitAllOpen(true);
  }, []);

  // Add Job (hamburger) → apm-select-jobs single-select MFE
  useEffect(() => {
    const handler = () => {
      setAddJobOpen(true);
    };
    window.addEventListener('apm:add-job', handler);
    return () => {
      return window.removeEventListener('apm:add-job', handler);
    };
  }, []);

  // Assign unknown job (e.g. UnknownJob footer) → same apm-select-jobs drawer as "Select Jobs"
  useEffect(() => {
    const handler = () => {
      setSelectJobsOpen(true);
    };
    window.addEventListener('apm:open-apm-select-jobs', handler);
    return () => {
      return window.removeEventListener('apm:open-apm-select-jobs', handler);
    };
  }, []);

  const closeSubmitAll = useCallback(() => {
    setSubmitAllOpen(false);
    setSubmitAllOpenedFromEnterProd(false);
  }, []);
  const closeSelectJobs = useCallback(() => {
    setSelectJobsOpen(false);
    window.dispatchEvent(new CustomEvent('apm:opdash-refresh'));
  }, []);
  const closeAddJob = useCallback(() => {
    setAddJobOpen(false);
    window.dispatchEvent(new CustomEvent('apm:opdash-refresh'));
  }, []);
  const handleReturnFromForm = useCallback(
    (reopenSubmitAll, didSubmit = false) => {
      if (openedFromSubmitAll) {
        setOpenedFromSubmitAll(false);
        if (reopenSubmitAll) {
          if (didSubmit) {
            const dismissId = pendingDismissIdRef.current;
            if (dismissId) {
              setDismissedItemIds((prev) => {
                return [...prev, dismissId];
              });
              pendingDismissIdRef.current = null;
            }
            setSubmitAllRefreshToken((token) => {
              return token + 1;
            });
            window.dispatchEvent(
              new CustomEvent('apm:opdash-refresh', {
                detail: { dismissItemId: dismissId },
              })
            );
            if (pendingItemCountRef.current > 1) {
              setSubmitAllOpen(true);
            }
          } else {
            setSubmitAllOpen(true);
          }
        }
      }
    },
    [openedFromSubmitAll]
  );

  const handleEventFormClose = useCallback(() => {
    setEventFormOpen(false);
    setEventFormContext(null);
    handleReturnFromForm(true, false);
  }, [handleReturnFromForm]);

  const handleEventFormSubmit = useCallback(() => {
    setEventFormOpen(false);
    setEventFormContext(null);
    handleReturnFromForm(true, true);
  }, [handleReturnFromForm]);

  useEmbeddedEffect(
    'labor-ticket-form-submit-all',
    'labor-ticket-screen:close',
    closeSubmitAll
  );

  useEmbeddedEffect(
    'labor-ticket-form-submit-all',
    'labor-ticket-form-submit-all:all-submitted',
    useCallback(async () => {
      if (submitAllOpenedFromEnterProd) {
        try {
          const { data } = await endSetupModeMutation({
            variables: { input: { machineRef } },
          });
          dispatch(actionSetSetupMode(data?.endSetupMode || null));
          window.dispatchEvent(new CustomEvent('apm:opdash-refresh'));
        } catch {
          // Mutation failed; remain in setup mode
        }
        setSubmitAllOpenedFromEnterProd(false);
        setSubmitAllOpen(false);
      } else if (submitAllOpenedFromSignOut) {
        if (isSetupMode) {
          try {
            const { data } = await endSetupModeMutation({
              variables: { input: { machineRef } },
            });
            dispatch(actionSetSetupMode(data?.endSetupMode || null));
          } catch {
            // Mutation failed; setup mode may persist but sign-out continues
          }
        }
        setSubmitAllOpenedFromSignOut(false);
        setSubmitAllOpen(false);
        requestOperatorLogout();
      } else {
        setSubmitAllOpen(false);
      }
    }, [
      submitAllOpenedFromEnterProd,
      submitAllOpenedFromSignOut,
      isSetupMode,
      endSetupModeMutation,
      machineRef,
      dispatch,
      requestOperatorLogout,
    ])
  );

  // When a ticket row is clicked, close submit-all drawer and open the appropriate form
  useEmbeddedEffect(
    'labor-ticket-form-submit-all',
    'labor-ticket-form-submit-all:open-ticket',
    useCallback(
      (data) => {
        if (!currentOperatorErpId) {
          throwErrorToast('Sign in to submit labor');
          return;
        }

        pendingItemCountRef.current = data?.itemCount || 0;
        pendingDismissIdRef.current = data?.eventId
          ? `event-${data.eventId}`
          : data?.laborTicketRef
          ? `lt-${data.laborTicketRef}`
          : null;

        const laborTicketAction =
          data.isSignOut && data.itemCount === 1
            ? LABOR_TICKET_ACTIONS.OPERATOR_LOGOUT
            : 'submit';

        if (data?.laborTicketRef) {
          setSubmitAllOpen(false);
          setOpenedFromSubmitAll(true);
          dispatch(
            actionSetEmbeddedLaborTicket({
              laborTicketRef: data.laborTicketRef,
              personId: currentOperatorErpId,
              workOrderId: data.workOrderId,
              sequenceNumber: data.sequenceNumber,
              resourceId: data.resourceId,
            })
          );
          dispatch(
            actionOpenLaborTicketModal(laborTicketAction, {
              isSignOut: !!data.isSignOut,
            })
          );
        } else if (data?.eventId && data?.machineRef) {
          // Program event without a labor ticket — open the event form
          const eventMachineMatchesCurrent =
            Number(data.machineRef) === Number(machineRef);
          setSubmitAllOpen(false);
          setOpenedFromSubmitAll(true);
          setEventFormContext({
            eventId: data.eventId,
            eventRefs: data.eventRefs,
            machineRef: data.machineRef,
            orderNumber: data.workOrderId,
            partNumber: data.partNumber,
            sequenceNumber: data.sequenceNumber,
            clockIn: data.clockIn,
            personId: currentOperatorErpId,
            workOrderOperationRef: data.workOrderOperationRef,
            type: data.ticketType || 'production',
            isAPMControlled: true,
            opened: true,
            action: laborTicketAction,
            isSignOut: !!data.isSignOut,
            timeOptionsContext: eventMachineMatchesCurrent
              ? timeOptionsContext
              : undefined,
          });
          setEventFormOpen(true);
        }
      },
      [dispatch, currentOperatorErpId, machineRef, timeOptionsContext]
    )
  );

  const onLaborTicketClose = useCallback(() => {
    handleReturnFromForm(true, false);
  }, [handleReturnFromForm]);
  const onLaborTicketSubmit = useCallback(() => {
    handleReturnFromForm(true, true);
  }, [handleReturnFromForm]);
  useEmbeddedEffect(
    'labor-ticket-form',
    'labor-ticket-form:close',
    onLaborTicketClose
  );
  useEmbeddedEffect(
    'labor-ticket-form',
    'labor-ticket-form:submit',
    onLaborTicketSubmit
  );
  useEmbeddedEffect(
    'labor-ticket-event-form',
    'labor-ticket-form:close',
    handleEventFormClose
  );
  useEmbeddedEffect(
    'labor-ticket-event-form',
    'labor-ticket-form:submit',
    handleEventFormSubmit
  );

  if (!operator) {
    return (
      <>
        <ControlBarResponsive id="job-control-bar">
          <UtilityMenu />
          <SingleButtonScrollable>
            {isErpDriven ? (
              // ERP-driven: jobs and labor are dispatched by the ERP, so
              // operators never sign in here — downtime categorization only
              <CategorizeDowntime history={history} machine={machine} />
            ) : (
              <ControlBarButton
                id="job-control-bar-sign-in"
                onClick={() => {
                  openSelectOperator();
                }}
              >
                <MobileIcon icon={user} />
                <ResponsiveText
                  full={t('signIn|full')}
                  medium={t('signIn|medium')}
                  short={t('signIn|short')}
                />
              </ControlBarButton>
            )}
          </SingleButtonScrollable>
        </ControlBarResponsive>
      </>
    );
  }

  return (
    <>
      <ControlBarResponsive id="job-control-bar">
        <UtilityMenu />
        <Scrollable>
          {isSetupMode ? (
            <>
              <ControlBarButton
                id="job-control-enter-prod"
                onClick={handleEnterProd}
              >
                <MobileIcon icon={sync} />
                <ResponsiveText
                  full="Enter Prod."
                  medium="Enter Prod."
                  short=""
                />
              </ControlBarButton>
              {hasOpenLaborTickets || hasCardsFromData ? (
                <ControlBarButton
                  id="job-control-submit-all"
                  onClick={handleSubmitAll}
                >
                  <MobileIcon icon={check} />
                  <ResponsiveText
                    full="Submit all"
                    medium="Submit all"
                    short=""
                  />
                </ControlBarButton>
              ) : (
                <ControlBarButton
                  id="job-control-select-jobs"
                  onClick={() => {
                    setSelectJobsOpen(true);
                  }}
                >
                  <MobileIcon icon={handPointer} />
                  <ResponsiveText
                    full="Select jobs"
                    medium="Select jobs"
                    short=""
                  />
                </ControlBarButton>
              )}
            </>
          ) : (
            <>
              <ControlBarButton
                id="job-control-enter-setup"
                onClick={handleEnterSetup}
              >
                <MobileIcon icon={sync} />
                <ResponsiveText
                  full="Enter Setup"
                  medium="Enter Setup"
                  short=""
                />
              </ControlBarButton>
              {(hasOpenLaborTickets || hasCardsFromData) && (
                <ControlBarButton
                  id="job-control-submit-all"
                  onClick={handleSubmitAll}
                >
                  <MobileIcon icon={check} />
                  <ResponsiveText
                    full="Submit all"
                    medium="Submit all"
                    short=""
                  />
                </ControlBarButton>
              )}
            </>
          )}

          <CategorizeDowntime history={history} machine={machine} />

          {/* ERP-driven: sign-out is handled by the ERP integration */}
          {!isErpDriven && (
            <ControlBarButton id="signout" onClick={handleSignOut}>
              <MobileIcon icon={signOut} />
              <ResponsiveText
                full={t('signout|full')}
                medium={t('signout|medium')}
                short={t('signout|short')}
              />
            </ControlBarButton>
          )}
        </Scrollable>
      </ControlBarResponsive>

      {/* Submit All drawer (sign-out or enter-prod gate) */}
      <EmbeddedMMDrawer
        embedZoneId="labor-ticket-form-submit-all"
        messageSource="labor-ticket-form"
        src={LABOR_TICKET_HOST}
        path="/submit-all"
        context={{
          machineRef,
          start: lookbackStart,
          personId: currentOperatorErpId,
          erpResources,
          refreshToken: submitAllRefreshToken,
          dismissedItemIds,
          isSignOut: submitAllOpenedFromSignOut,
          setupMode: isSetupMode,
          programEvents,
          timeOptionsContext,
        }}
        isOpen={submitAllOpen}
        onDismiss={closeSubmitAll}
        defaultTitle="Submit all work"
      />

      {/* Select Jobs drawer (multi-select) — apm-select-jobs MFE */}
      <MMDrawer
        isOpen={selectJobsOpen}
        onClose={closeSelectJobs}
        title="Select Jobs"
        hideHeader
      >
        <Box
          flex={1}
          minH={0}
          display="flex"
          flexDirection="column"
          overflow="hidden"
          h="calc(100vh - 2rem)"
        >
          <MultiJobSelectFlow
            machineRef={machineRef}
            machineId={machine?.id}
            erpResources={erpResources}
            onCancel={closeSelectJobs}
            onComplete={closeSelectJobs}
          />
        </Box>
      </MMDrawer>

      {/* Add Job drawer (single-select) — apm-select-jobs MFE */}
      <MMDrawer
        isOpen={addJobOpen}
        onClose={closeAddJob}
        title="Add Job"
        hideHeader
      >
        <Box
          flex={1}
          minH={0}
          display="flex"
          flexDirection="column"
          overflow="hidden"
          h="calc(100vh - 2rem)"
        >
          <SingleSelectJobFlow
            machineRef={machineRef}
            machineId={machine?.id}
            erpResources={erpResources}
            onCancel={closeAddJob}
            onComplete={closeAddJob}
          />
        </Box>
      </MMDrawer>

      {/* Program event form drawer */}
      <MMDrawer
        isOpen={eventFormOpen}
        onClose={handleEventFormClose}
        title="Submit work"
      >
        {eventFormContext && (
          <MMEmbeddableZoneWithLoader
            id="labor-ticket-event-form"
            src={LABOR_TICKET_HOST}
            path={`/program-event/${eventFormContext.machineRef}/${eventFormContext.eventId}`}
            context={eventFormContext}
          />
        )}
      </MMDrawer>
    </>
  );
};

export { ERPMultiJobControlBar };
